import React, { useCallback, useContext, useRef, useState } from 'react';
import { Box, Grid, TextField, Autocomplete } from '@mui/material';
import { useTheme } from '@emotion/react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import MuiDialog from '../../SystemDesign/MuiDialog';
import FormInput from '../../SystemDesign/FormInput';
import { tokens } from '../../theme';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_QUIZ_ASSIGNMENT } from '../../Reducers/Actions';
import { useCreateQuizAssignmentMutation, useGetQuizzesQuery, useGetUsersQuery } from '../../services/SkillsManagement';

// Validation schema
const dateTransform = (value, originalValue) => {
  // Handle MUI datetime-local which provides '' when empty
  if (originalValue === '' || originalValue === undefined || originalValue === null) return null;
  // If already a Date, keep it; else parse string
  if (value instanceof Date && !isNaN(value)) return value;
  const parsed = new Date(originalValue);
  return isNaN(parsed) ? value : parsed;
};

const validationSchema = Yup.object({
  quizId: Yup.number()
    .typeError('Quiz ID must be a number')
    .required('Quiz ID is required')
    .positive('Quiz ID must be positive')
    .integer('Quiz ID must be an integer'),
  assigneeUserId: Yup.number()
    .typeError('Assignee user ID must be a number')
    .required('Assignee user ID is required')
    .positive('Assignee user ID must be positive')
    .integer('Assignee user ID must be an integer'),
  note: Yup.string().max(500, 'Note is too long').nullable(),
  dueAt: Yup.date().transform(dateTransform).nullable().typeError('Invalid date'),
  expiresAt: Yup.date()
    .transform(dateTransform)
    .nullable()
    .typeError('Invalid date')
    .test('is-after-due', 'Expires at must be after Due at', function (value) {
      const { dueAt } = this.parent || {};
      if (!value || !dueAt) return true; // only validate when both are set
      const v = value instanceof Date ? value : new Date(value);
      const d = dueAt instanceof Date ? dueAt : new Date(dueAt);
      if (isNaN(v) || isNaN(d)) return true; // skip if any invalid
      return v >= d;
    }),
});

const CreateAssignement = ({ open, onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);

  // RTK Query mutation
  const [createAssignment, { isLoading }] = useCreateQuizAssignmentMutation();

  // Load quizzes (titles) and users for selection
  const quizzesArgs = { page: 1, limit: 100, isActive: true };
  const { data: quizzesData, isLoading: loadingQuizzes } = useGetQuizzesQuery(
    quizzesArgs,
    { refetchOnMountOrArgChange: false }
  );
  const quizzesOptions = (quizzesData?.data?.quizzes || quizzesData?.quizzes || [])
    .map((q) => ({ id: Number(q?.quizId ?? q?.id), title: String(q?.title || `Quiz #${q?.quizId ?? q?.id}`) }));

  const usersArgs = {
    filters: '',
    sorting: '',
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 50 }),
    type: '',
  };
  const { data: usersData, isLoading: loadingUsers } = useGetUsersQuery(
    usersArgs,
    { refetchOnMountOrArgChange: false }
  );
  const usersOptions = (usersData?.users || usersData?.data?.users || usersData || [])
    .map((u) => ({ id: Number(u?.id), label: String(u?.username ?? u?.email ?? u?.fullName ?? u?.name ?? `User #${u?.id}`) }));

  // Track form validity/dirty to control dialog action button
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Formik ref to trigger submit from dialog action
  const formRef = useRef(null);

  // Initial values
  const [formValues] = useState({
    quizId: '',
    assigneeUserId: '',
    note: '',
    dueAt: '',      // ISO date string or empty
    expiresAt: '',  // ISO date string or empty
  });

  // Grid sizing similar to AddEmployee.getFieldGridProps
  const getFieldGridProps = (name) => {
    switch (name) {
      case 'quizId':
      case 'assigneeUserId':
        return { xs: 12, sm: 6, md: 6, lg: 6 };
      case 'dueAt':
      case 'expiresAt':
        return { xs: 12, sm: 6, md: 6, lg: 6 };
      case 'note':
        return { xs: 12, sm: 12, md: 12, lg: 12 };
      default:
        return { xs: 12, sm: 6, md: 6 };
    }
  };

  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      setSubmitting(true);

      const payload = {
        quizId: Number(values.quizId),
        assigneeUserId: Number(values.assigneeUserId),
        ...(values.note ? { note: values.note } : {}),
        ...(values.dueAt ? { dueAt: new Date(values.dueAt).toISOString() } : {}),
        ...(values.expiresAt ? { expiresAt: new Date(values.expiresAt).toISOString() } : {}),
      };

      const result = await createAssignment({ body: payload }).unwrap();
      // API types show: { success: true, data: { assignmentId, quizId, ... } }
      const created = result?.data || result;

      // Dispatch to Redux
      dispatch({ type: ADD_QUIZ_ASSIGNMENT, payload: created });

      // Inform parent
      if (onSubmit) onSubmit(created);

      // Snackbar success
      setInfoAlert({
        severity: 'success',
        message: 'Quiz assignment successfully created.',
      });
      setOpenSnackBar(true);

      // Reset & close
      resetForm();
      onClose();
      // Notify assignment lists to refresh immediately
      try { window.dispatchEvent(new CustomEvent('quiz-assignments:changed')); } catch {}
    } catch (error) {
      console.error('Error creating quiz assignment:', error);
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to create quiz assignment.',
      });
      setOpenSnackBar(true);
    } finally {
      setSubmitting(false);
    }
  };

  const dialogContent = () => (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount
      enableReinitialize={false}
    >
      {(formik) => {
        const { values, errors, touched, handleChange, handleBlur, isValid, dirty } = formik;

        // Keep action button state in sync
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }

        return (
          <Form id="create-assignment-form">
            <Grid container spacing={2}>
              <Grid item size={{ ...getFieldGridProps('quizId') }}>
                <Autocomplete
                  options={quizzesOptions}
                  loading={loadingQuizzes}
                  getOptionLabel={(o) => String(o?.title || '')}
                  value={quizzesOptions.find((o) => Number(o.id) === Number(values.quizId)) || null}
                  onChange={(_, opt) => formik.setFieldValue('quizId', opt?.id || '')}
                  isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Quiz"
                      placeholder="Select a quiz"
                      error={touched.quizId && Boolean(errors.quizId)}
                      helperText={(touched.quizId && errors.quizId) || ' '}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ ...getFieldGridProps('assigneeUserId') }}>
                <Autocomplete
                  options={usersOptions}
                  loading={loadingUsers}
                  getOptionLabel={(o) => String(o?.label || '')}
                  value={usersOptions.find((o) => Number(o.id) === Number(values.assigneeUserId)) || null}
                  onChange={(_, opt) => formik.setFieldValue('assigneeUserId', opt?.id || '')}
                  isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Assignee"
                      placeholder="Select a user"
                      error={touched.assigneeUserId && Boolean(errors.assigneeUserId)}
                      helperText={(touched.assigneeUserId && errors.assigneeUserId) || ' '}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ ...getFieldGridProps('dueAt') }}>
                <FormInput
                  name="dueAt"
                  label="Due Date (optional)"
                  type="date"
                />
              </Grid>

              <Grid item size={{ ...getFieldGridProps('expiresAt') }}>
                <FormInput
                  name="expiresAt"
                  label="Expiration Date (optional)"
                  type="date"
                />
              </Grid>

              <Grid item size={{ ...getFieldGridProps('note') }}>
                <TextField
                  fullWidth
                  label="Note (optional)"
                  name="note"
                  value={values.note}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.note && Boolean(errors.note)}
                  helperText={(touched.note && errors.note) || ' '}
                  placeholder="Add an optional note"
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      icon={<AddIcon />}
      title="Create New Assignment"
      subtitle="Assign a quiz to a user"
      content={dialogContent}
      existAction={true}
      actionTitle="Create Assignment"
      actionFunction={submitForm}
      isLoading={isLoading}
      actionType="submit"
      formId="create-assignment-form"
      disableAction={!(isFormValid && isFormDirty)}
    />
  );
};

export default CreateAssignement;